import { FC } from 'react';
import {
  Analysts,
  Announcement,
  ESGCoverage,
  Home,
  Instrument,
  Entity,
  IssuerDashboard,
  NewsCentre,
  Profile,
  RatingCoverage,
  RatingCoverageRelated,
  SearchResult,
  RatingCoverageIndustry,
  EntityDetails,
  GenerateReportESG,
  ErrorPage,
  ServiceNotAvailable,
  RestrictedSubscriptionRatings,
  CoveredBonds,
  CreditRatingCoverage,
} from 'scenes';

import { Shop } from 'modules/shop/scenes';
import { EventPanelCreate } from 'modules/events/scenes/EventPanelCreate';
import { EventPanelView } from 'modules/events/scenes/EventPanelView';
import { licencesTypes } from 'shared/constants/licencesTypes';
import { permissionsTypes } from 'shared/constants/permissionsTypes';
import { allStandardRolesTypes } from 'shared/constants/rolesTypes';
import { PortfolioManager } from 'modules/common/PortfolioManager/PortfolioManager';
import { Portfolio } from 'modules/common/PortfolioManager/Portfolio/Portfolio';
import { ShopPanelCreator } from 'modules/shop/scenes/ShopPanel/ShopPanelCreator';
import { ShopPanelEdit } from 'modules/shop/scenes/ShopPanel/ShopPanelEdit';
import { ShopPanelProductList } from 'modules/shop/scenes/ShopPanel/ShopPanelProductList';
import { shopRoles } from 'modules/shop/shared/constants/shopRoles';
import { Events } from 'modules/events/scenes/Events';
import { RatingUsersList } from './modules/common/RestrictedSubscriptionRatings/RatingUsersList';

const getEntitiesRoutings = (
  baseAddress: string,
  component: FC,
  role: string | string[],
  license: string,
) => [
  {
    path: `${baseAddress}/entities/:entityId/instruments/:instrumentId/announcements/:announcementId`,
    component: Announcement,
    license,
    role,
  },
  {
    path: `${baseAddress}/entities/:entityId/instruments/:instrumentId`,
    component: Instrument,
    license,
    role,
  },
  {
    path: `${baseAddress}/entities/:entityId/announcements/:announcementId`,
    component: Announcement,
    license,
    role,
  },
  {
    path: `${baseAddress}/entities/:entityId`,
    component: Entity,
    license,
    role,
  },
  {
    path: baseAddress,
    component,
    license,
    role,
  },
];

const routes = [
  {
    path: '/',
    exact: true,
    component: Home,
    license: licencesTypes.INVESTORHUB_MODULE,
  },
  {
    path: '/issuer-dashboard',
    component: IssuerDashboard,
    license: licencesTypes.INVESTORHUB_MODULE,
    shopPermissions: [shopRoles.ISSUER_DASHBOARD],
  },
  {
    path: '/analysts',
    component: Analysts,
    license: licencesTypes.INVESTORHUB_MODULE,
    permission: permissionsTypes.SSO_VIEW_USER_ANALYST_DATA,
  },
  {
    path: '/watchlists',
    component: PortfolioManager,
    license: licencesTypes.INVESTORHUB_MODULE,
    permission: permissionsTypes.CA_VIEW_DOSSIER,
  },
  {
    path: '/instruments/:instrumentId',
    component: Instrument,
    license: licencesTypes.INVESTORHUB_MODULE,
  },
  {
    path: '/entities/:easyNumber/details',
    component: EntityDetails,
    license: licencesTypes.INVESTORHUB_MODULE,
  },
  {
    path: '/entities/:entityId/instruments/:instrumentId',
    component: Instrument,
    license: licencesTypes.INVESTORHUB_MODULE,
  },
  {
    path: '/entities/:entityId/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
  },
  {
    path: '/entities/:entityId',
    component: Entity,
    license: licencesTypes.INVESTORHUB_MODULE,
  },
  {
    path: '/news-centre',
    component: NewsCentre,
    license: licencesTypes.INVESTORHUB_MODULE,
  },
  {
    path: '/profile',
    component: Profile,
    license: licencesTypes.INVESTORHUB_MODULE,
  },
  {
    path: '/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
  },
  {
    path: '/generate-report/esg/:entityId',
    component: GenerateReportESG,
  },
  // Covered Bonds
  {
    path: '/covered-bonds/:country/instruments/:instrumentId',
    component: Instrument,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  // Search result
  {
    path: '/search-result/entities/:easyNumber/details',
    component: EntityDetails,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/search-result/entities/:entityId/instruments/:instrumentId/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/search-result/entities/:entityId/instruments/:instrumentId',
    component: Instrument,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/search-result/entities/:entityId/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/search-result/entities/:entityId',
    component: Entity,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/search-result/instruments/:instrumentId',
    component: Instrument,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/search-result/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/search-result',
    component: SearchResult,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  // /Search result
  // Portfolios
  {
    path: '/portfolios/:portfolioId/entities/:entityId/instruments/:instrumentId/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/portfolios/:portfolioId/entities/:entityId/instruments/:instrumentId',
    component: Instrument,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/portfolios/:portfolioId/entities/:entityId/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/portfolios/:portfolioId/entities/:easyNumber/details',
    component: EntityDetails,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/portfolios/:portfolioId/entities/:entityId',
    component: Entity,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/portfolios/:portfolioId/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/portfolios/:portfolioId/instruments/:instrumentId',
    component: Instrument,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/portfolios/:portfolioId',
    component: Portfolio,
    license: licencesTypes.INVESTORHUB_MODULE,
    permission: permissionsTypes.CA_VIEW_DOSSIER,
  },
  {
    path: '/portfolios',
    component: PortfolioManager,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  // /Portfolios
  // Related rating coverage related
  {
    path: '/credit-rating-coverage/related/entities/:entityId/instruments/:instrumentId/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/credit-rating-coverage/related/entities/:entityId/instruments/:instrumentId',
    component: Instrument,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/credit-rating-coverage/related/entities/:entityId/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/credit-rating-coverage/related/entities/:entityId',
    component: Entity,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/credit-rating-coverage/related',
    component: RatingCoverageRelated,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  // /Related rating coverage related
  // Related rating coverage industry
  {
    path: '/credit-rating-coverage/industry/entities/:entityId/instruments/:instrumentId/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/credit-rating-coverage/industry/entities/:entityId/instruments/:instrumentId',
    component: Instrument,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/credit-rating-coverage/industry/entities/:entityId/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/credit-rating-coverage/industry/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/credit-rating-coverage/industry/entities/:entityId',
    component: Entity,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/credit-rating-coverage/industry',
    component: RatingCoverageIndustry,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  // /Related rating coverage industry
  // Related rating coverage
  {
    path: '/credit-rating-coverage/:businessLine/entities/:entityId/instruments/:instrumentId/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/credit-rating-coverage/:businessLine/entities/:entityId/instruments/:instrumentId',
    component: Instrument,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/credit-rating-coverage/:businessLine/entities/:entityId/announcements/:announcementId',
    component: Announcement,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/credit-rating-coverage/:businessLine/entities/:entityId',
    component: Entity,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/credit-rating-coverage/:businessLine',
    component: RatingCoverage,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  ...getEntitiesRoutings(
    '/credit-rating-coverage-list/:countryId',
    CreditRatingCoverage,
    allStandardRolesTypes,
    licencesTypes.INVESTORHUB_MODULE,
  ),
  // /Related rating coverage
  ...getEntitiesRoutings(
    '/esg-coverage/:query',
    ESGCoverage,
    allStandardRolesTypes,
    licencesTypes.INVESTORHUB_MODULE,
  ),
  {
    path: '/subscriptions/panel/create',
    component: ShopPanelCreator,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
    shopPermissions: [shopRoles.SHOP_ADMIN],
  },
  {
    path: '/subscriptions/panel',
    component: ShopPanelProductList,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
    shopPermissions: [shopRoles.SHOP_ADMIN],
  },
  {
    path: '/subscriptions/:productId',
    component: ShopPanelEdit,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
    shopPermissions: [shopRoles.SHOP_ADMIN],
  },
  {
    path: '/subscriptions',
    component: Shop,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/error-page',
    exact: true,
    component: ErrorPage,
    license: licencesTypes.INVESTORHUB_MODULE,
  },
  {
    path: '/service-unavailable',
    exact: true,
    component: ServiceNotAvailable,
    license: licencesTypes.INVESTORHUB_MODULE,
  },
  {
    path: '/event-panel/view',
    component: EventPanelView,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
    shopPermissions: [shopRoles.SHOP_ADMIN],
  },
  {
    path: '/event-panel/create',
    component: EventPanelCreate,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
    shopPermissions: [shopRoles.SHOP_ADMIN],
  },
  {
    path: '/event-panel/edit/:id',
    component: EventPanelCreate,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
    shopPermissions: [shopRoles.SHOP_ADMIN],
  },
  {
    path: '/events',
    component: Events,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/covered-bonds/:countryId',
    component: CoveredBonds,
    license: licencesTypes.INVESTORHUB_MODULE,
    role: allStandardRolesTypes,
  },
  {
    path: '/admin-panel/:ratingType/ratings/:ratingId',
    component: RatingUsersList,
    license: licencesTypes.INVESTORHUB_MODULE,
    // permission: permissionsTypes.CA_RSR_CONFIG,
  },
  {
    path: '/admin-panel/:ratingType/:tabSelected',
    component: RestrictedSubscriptionRatings,
    license: licencesTypes.INVESTORHUB_MODULE,
    // permission: permissionsTypes.CA_RSR_CONFIG,
  },
  {
    path: '/admin-panel/:ratingType',
    component: RestrictedSubscriptionRatings,
    license: licencesTypes.INVESTORHUB_MODULE,
    // permission: permissionsTypes.CA_RSR_CONFIG,
  },
  {
    path: '/admin-panel',
    component: RestrictedSubscriptionRatings,
    license: licencesTypes.INVESTORHUB_MODULE,
    // permission: permissionsTypes.CA_RSR_CONFIG,
  },
];

export { routes };
